import { useMemo } from 'react';

import { Native } from '@givebacks/utils';
import {
  browserName,
  isAndroid,
  isChrome,
  isChromium,
  isDesktop,
  isEdge,
  isIOS,
  isSafari,
} from 'react-device-detect';

// Why did I not just have this create during run time... ugh.

type DesktopPlatforms = 'chrome' | 'safari' | 'edge' | 'chromium' | 'brave';

type PlatformOSType = DesktopPlatforms | 'native' | 'mobileBrowser' | 'default';

type MobileBrowser = 'ios' | 'android' | false;

type IsDesktopBrowser = DesktopPlatforms | boolean;

type PlatformSpecific = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
  [platform in PlatformOSType]?: void | any;
};

interface IPlatform {
  OS: PlatformOSType;
  isNative: boolean;
  select<T>(
    specifics:
      | ({ [platform in PlatformOSType]?: T } & { default: T })
      | { [platform in PlatformOSType]: T }
  ): T;
  mobileBrowser: MobileBrowser;
  isDesktopBrowser: IsDesktopBrowser;
  browserName: DesktopPlatforms;
}

export function usePlatformService() {
  const isMobileBrowser = useMemo((): MobileBrowser => {
    switch (true) {
      case isIOS:
        return 'ios';
      case isAndroid:
        return 'android';
      default:
        return false;
    }
  }, []);

  const platformOs = useMemo((): PlatformOSType => {
    switch (true) {
      case !!Native.WebView:
        return 'native';
      case !!isMobileBrowser:
        return 'mobileBrowser';
      case isDesktop && browserName == 'Brave':
        return 'brave';
      case isDesktop && isChrome:
        return 'chrome';
      case isDesktop && isSafari:
        return 'safari';
      case isDesktop && isChromium:
        return 'chromium';
      case isDesktop && isEdge:
        return 'edge';

      default:
        return 'default';
    }
  }, []);

  const isDesktopBrowser = useMemo((): IsDesktopBrowser => {
    switch (platformOs) {
      case 'native':
      case 'mobileBrowser':
        return false;
      case 'default':
        return true;
      default:
        return platformOs;
    }
  }, [platformOs]);

  const Platform: IPlatform = {
    OS: platformOs,
    isNative: platformOs == 'native',
    mobileBrowser: isMobileBrowser,
    isDesktopBrowser: isDesktopBrowser,
    select: (specifics: PlatformSpecific) => {
      return specifics[platformOs] ?? specifics.default;
    },
    browserName: browserName as DesktopPlatforms,
  };

  return Platform;
}
